// Treatment areas data with content
export const treatmentAreas = [
  {
    slug: 'carpal-tunnel-syndrome',
    title: 'Carpal Tunnel Syndrome',
    shortDescription: 'Numbness, tingling, and weakness in the hand caused by pressure on the median nerve.',
    overview: 'Carpal tunnel syndrome (CTS) is a condition brought on by increased pressure on the median nerve at the wrist. The median nerve provides sensation to the thumb, index, middle, and ring fingers. When pressure builds up in the carpal tunnel—a narrow passageway in the wrist—it can cause numbness, tingling, and weakness in the hand.',
    symptoms: [
      'Numbness or tingling in thumb, index, middle, and ring fingers',
      'Pain or burning sensation that travels up the arm',
      'Weakness in the hand and difficulty gripping objects',
      'Symptoms often worse at night',
      'Dropping things more frequently'
    ],
    causes: [
      'Repetitive hand movements',
      'Wrist injuries or fractures',
      'Rheumatoid arthritis',
      'Diabetes',
      'Pregnancy',
      'Thyroid dysfunction'
    ],
    treatments: [
      'Wrist splinting, especially at night',
      'Anti-inflammatory medications',
      'Corticosteroid injections',
      'Minimally invasive carpal tunnel release surgery',
      'Physical therapy and exercises'
    ],
    icon: 'Hand'
  },
  {
    slug: 'cubital-tunnel-syndrome',
    title: 'Cubital Tunnel Syndrome',
    shortDescription: 'Compression of the ulnar nerve at the elbow causing numbness in the ring and small fingers.',
    overview: 'Cubital tunnel syndrome occurs when the ulnar nerve—commonly known as the "funny bone" nerve—becomes compressed or irritated at the elbow. This nerve runs through a tunnel of tissue called the cubital tunnel on the inside of the elbow.',
    symptoms: [
      'Numbness and tingling in the ring and small fingers',
      'Pain on the inside of the elbow',
      'Weakness in the hand',
      'Difficulty with finger coordination',
      'Symptoms worse when elbow is bent'
    ],
    causes: [
      'Prolonged elbow bending',
      'Leaning on the elbow for extended periods',
      'Previous elbow injuries',
      'Bone spurs or arthritis',
      'Anatomical variations'
    ],
    treatments: [
      'Avoiding pressure on the elbow',
      'Elbow padding or splinting',
      'Anti-inflammatory medications',
      'Nerve gliding exercises',
      'Cubital tunnel release surgery'
    ],
    icon: 'Zap'
  },
  {
    slug: 'dupuytrens-contractures',
    title: "Dupuytren's Contractures",
    shortDescription: 'Abnormal thickening of tissue beneath the skin causing fingers to bend toward the palm.',
    overview: "Dupuytren's disease is an abnormal thickening of the tissue just beneath the skin of the palm. This thickening can develop into hard nodules and cords that may cause the fingers to bend toward the palm, making it difficult to straighten them.",
    symptoms: [
      'Nodules or lumps in the palm',
      'Thickened skin on the palm',
      'Fingers gradually bending toward palm',
      'Difficulty laying hand flat on a surface',
      'Reduced hand function'
    ],
    causes: [
      'Genetic predisposition',
      'Northern European ancestry',
      'Age (more common over 50)',
      'Diabetes',
      'Smoking and alcohol use'
    ],
    treatments: [
      'Observation for mild cases',
      'Needle aponeurotomy',
      'Collagenase injections',
      'Surgical fasciectomy',
      'Hand therapy after treatment'
    ],
    icon: 'Activity'
  },
  {
    slug: 'finger-dislocations',
    title: 'Finger Dislocations',
    shortDescription: 'Displacement of finger bones from their normal position at the joint.',
    overview: 'A finger dislocation occurs when the bones of the finger are forced out of their normal position at the joint. This can happen from sports injuries, falls, or accidents. Prompt treatment is important to prevent long-term complications.',
    symptoms: [
      'Visible deformity of the finger',
      'Severe pain and swelling',
      'Inability to move the finger',
      'Numbness or tingling',
      'Bruising around the joint'
    ],
    causes: [
      'Sports injuries (basketball, football, volleyball)',
      'Falls onto outstretched hand',
      'Work-related accidents',
      'Motor vehicle accidents',
      'Direct trauma to the finger'
    ],
    treatments: [
      'Closed reduction (realignment without surgery)',
      'Splinting or buddy taping',
      'X-rays to confirm alignment',
      'Surgery for complex dislocations',
      'Physical therapy for recovery'
    ],
    icon: 'MousePointer'
  },
  {
    slug: 'finger-fractures',
    title: 'Finger Fractures',
    shortDescription: 'Broken bones in the fingers requiring proper alignment for optimal healing.',
    overview: 'Finger fractures are breaks in the small bones (phalanges) of the fingers. These injuries are common and can occur from various causes. Proper treatment is essential to restore finger function and prevent deformity.',
    symptoms: [
      'Pain and tenderness',
      'Swelling and bruising',
      'Difficulty moving the finger',
      'Visible deformity or misalignment',
      'Numbness if nerves are affected'
    ],
    causes: [
      'Direct impact or crush injuries',
      'Falls',
      'Sports injuries',
      'Work-related accidents',
      'Twisting injuries'
    ],
    treatments: [
      'Splinting for stable fractures',
      'Buddy taping',
      'Surgical fixation with pins or screws',
      'Cast immobilization',
      'Hand therapy for rehabilitation'
    ],
    icon: 'Bone'
  },
  {
    slug: 'finger-sprains',
    title: 'Finger Sprains',
    shortDescription: 'Ligament injuries in the fingers causing pain, swelling, and limited motion.',
    overview: 'A finger sprain occurs when the ligaments that support the finger joints are stretched or torn. This common injury can range from mild stretching to complete ligament tears and requires appropriate treatment for proper healing.',
    symptoms: [
      'Pain at the injured joint',
      'Swelling and tenderness',
      'Bruising',
      'Difficulty bending or straightening',
      'Joint instability in severe cases'
    ],
    causes: [
      'Jamming the finger',
      'Sports injuries',
      'Falls',
      'Hyperextension injuries',
      'Twisting motions'
    ],
    treatments: [
      'Rest, ice, compression, elevation (RICE)',
      'Buddy taping to adjacent finger',
      'Splinting',
      'Anti-inflammatory medications',
      'Surgery for complete ligament tears'
    ],
    icon: 'Circle'
  },
  {
    slug: 'ganglion-cyst',
    title: 'Ganglion Cyst',
    shortDescription: 'Fluid-filled lumps that commonly develop on the wrist or hand.',
    overview: 'Ganglion cysts are noncancerous lumps that most commonly develop along the tendons or joints of the wrists or hands. They are filled with a thick, jelly-like fluid and can vary in size. While often painless, they can cause discomfort if they press on nearby nerves.',
    symptoms: [
      'Visible lump on wrist or hand',
      'Size may change with activity',
      'Pain or discomfort if pressing on nerves',
      'Weakness in affected area',
      'Tingling or numbness'
    ],
    causes: [
      'Joint or tendon irritation',
      'Repetitive stress',
      'Trauma or injury',
      'Osteoarthritis',
      'Unknown causes in many cases'
    ],
    treatments: [
      'Observation (may resolve on its own)',
      'Immobilization with splint',
      'Aspiration (draining the fluid)',
      'Surgical removal',
      'Minimally invasive techniques'
    ],
    icon: 'Circle'
  },
  {
    slug: 'dequervains-tenosynovitis',
    title: "de Quervain's Tenosynovitis",
    shortDescription: 'Painful condition affecting tendons on the thumb side of the wrist.',
    overview: "de Quervain's tenosynovitis is a painful condition affecting the tendons on the thumb side of the wrist. It occurs when the tendons become irritated or constricted, causing pain with thumb and wrist movement.",
    symptoms: [
      'Pain near the base of the thumb',
      'Swelling near the wrist',
      'Difficulty gripping or pinching',
      'Pain when making a fist',
      'Catching or snapping sensation'
    ],
    causes: [
      'Repetitive hand or wrist movements',
      'Direct injury to the wrist',
      'Inflammatory conditions',
      'New parents (lifting baby frequently)',
      'Activities requiring forceful gripping'
    ],
    treatments: [
      'Rest and activity modification',
      'Splinting the thumb and wrist',
      'Anti-inflammatory medications',
      'Corticosteroid injections',
      'Surgical release'
    ],
    icon: 'Hand'
  },
  {
    slug: 'hand-fractures',
    title: 'Hand Fractures',
    shortDescription: 'Broken bones in the hand requiring expert care for proper healing.',
    overview: 'Hand fractures involve breaks in the metacarpal bones of the hand. These injuries can result from various causes and require proper treatment to ensure optimal healing and preservation of hand function.',
    symptoms: [
      'Severe pain and swelling',
      'Bruising and tenderness',
      'Deformity or misalignment',
      'Difficulty moving the hand',
      'Grinding sensation with movement'
    ],
    causes: [
      'Punching or striking objects',
      'Falls onto outstretched hand',
      'Crush injuries',
      'Sports injuries',
      'Motor vehicle accidents'
    ],
    treatments: [
      'Casting or splinting',
      'Closed reduction',
      'Surgical fixation',
      'Physical therapy',
      'Follow-up X-rays'
    ],
    icon: 'Bone'
  },
  {
    slug: 'mallet-finger',
    title: 'Mallet Finger',
    shortDescription: 'Injury to the tendon that straightens the tip of the finger.',
    overview: 'Mallet finger, sometimes called baseball finger, is an injury to the tendon that straightens the tip of the finger or thumb. It typically occurs when the fingertip is forcibly bent, tearing or stretching the extensor tendon.',
    symptoms: [
      'Drooping fingertip that cannot straighten',
      'Pain and swelling at fingertip',
      'Tenderness over the joint',
      'Possible bruising',
      'Difficulty with fine motor tasks'
    ],
    causes: [
      'Ball sports (baseball, basketball, football)',
      'Direct trauma to fingertip',
      'Jamming finger against hard surface',
      'Crush injuries',
      'Work-related accidents'
    ],
    treatments: [
      'Splinting the fingertip straight',
      'Continuous splint wear for 6-8 weeks',
      'Surgery for certain fracture types',
      'Hand therapy',
      'Follow-up care'
    ],
    icon: 'MousePointer'
  },
  {
    slug: 'wrist-fractures',
    title: 'Wrist Fractures',
    shortDescription: 'Broken bones in the wrist, commonly from falls onto an outstretched hand.',
    overview: 'Wrist fractures are among the most common bone breaks, often occurring when someone falls onto an outstretched hand. The distal radius is the most commonly fractured bone. Proper treatment is essential for restoring wrist function.',
    symptoms: [
      'Immediate pain and swelling',
      'Visible deformity',
      'Bruising',
      'Difficulty moving wrist',
      'Numbness or tingling'
    ],
    causes: [
      'Falls onto outstretched hand',
      'Sports injuries',
      'Motor vehicle accidents',
      'Osteoporosis (increases fracture risk)',
      'Direct impact injuries'
    ],
    treatments: [
      'Casting for stable fractures',
      'Closed reduction',
      'Surgical fixation with plates and screws',
      'External fixation',
      'Physical therapy for rehabilitation'
    ],
    icon: 'Bone'
  },
  {
    slug: 'wrist-sprains',
    title: 'Wrist Sprains',
    shortDescription: 'Ligament injuries in the wrist causing pain and instability.',
    overview: 'A wrist sprain occurs when the ligaments in the wrist are stretched or torn, usually from a fall or sudden twisting motion. Sprains can range from mild to severe and require appropriate treatment for proper healing.',
    symptoms: [
      'Pain and swelling',
      'Bruising around the wrist',
      'Tenderness to touch',
      'Limited range of motion',
      'Feeling of instability'
    ],
    causes: [
      'Falls onto outstretched hand',
      'Sports injuries',
      'Twisting or bending wrist forcefully',
      'Motor vehicle accidents',
      'Work-related injuries'
    ],
    treatments: [
      'Rest, ice, compression, elevation (RICE)',
      'Splinting or bracing',
      'Anti-inflammatory medications',
      'Physical therapy',
      'Surgery for severe ligament tears'
    ],
    icon: 'Activity'
  },
  {
    slug: 'trigger-finger',
    title: 'Trigger Finger',
    shortDescription: 'Condition causing finger to catch or lock when bent.',
    overview: 'Trigger finger occurs when the tendon sheath in a finger becomes inflamed and narrows, preventing the tendon from gliding smoothly. This causes the finger to catch, pop, or lock when bent.',
    symptoms: [
      'Finger catching or locking when bent',
      'Popping or clicking sensation',
      'Stiffness, especially in the morning',
      'Tender nodule at the base of the finger',
      'Finger stuck in bent position'
    ],
    causes: [
      'Repetitive gripping motions',
      'Rheumatoid arthritis',
      'Diabetes',
      'Gout',
      'Certain occupations requiring gripping'
    ],
    treatments: [
      'Rest and activity modification',
      'Splinting at night',
      'Anti-inflammatory medications',
      'Corticosteroid injections',
      'Trigger finger release surgery'
    ],
    icon: 'MousePointer'
  }
];

export const getTreatmentBySlug = (slug) => {
  return treatmentAreas.find(t => t.slug === slug);
};
